import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { addToFavorites } from "./favorisSlice";

// --- Initial state ---
const initialState = {
  query: "",
  location: "",
  how: [],
  when: "all_time",
  results: [],
  googleResults: [],
  autocompleteSuggestions: [],
  loading: false,
  loadingSearch: false,
  error: null,
  categoryId: null,
  categoryName: "",
  lat: null,
  lng: null,
  sortBy: "relevance",
  cityName: "",
  filtre: [],
  saisie: false,
  per_page: 10,
  last_page: '',
  page: 1,
  total: 0, 
  // Store all current search parameters
  currentSearchParams: {},
};

const token = localStorage.getItem("token");

// --- Thunks ---
export const fetchLocationCoords = createAsyncThunk(
  "search/fetchLocationCoords",
  async (location, { rejectWithValue }) => {
    try {
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
      const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
        location
      )}&key=${apiKey}`;
      const response = await axios.get(url);

      if (response.data.status !== "OK") {
        throw new Error(response.data.error_message || "Geocoding API error");
      }

      const { lat, lng } = response.data.results[0].geometry.location;
      return { lat, lng };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchSearchResults = createAsyncThunk(
  "search/fetchSearchResults",
  async (overrideParams = {}, { rejectWithValue, dispatch, getState }) => {
    try {
      const state = getState();
      const searchState = state.search;

      // Fusion params
      const params = {
        ...searchState.currentSearchParams,
        ...overrideParams,
      };

      // Si location mais pas coords → fetch
      if (params.location && (!params.lat || !params.lng)) {
        const coords = await dispatch(fetchLocationCoords(params.location)).unwrap();
        params.lat = coords.lat;
        params.lng = coords.lng;
      }
      // console.log(params.page)
      const apiUrl = `${process.env.REACT_APP_API_URL}etablissements/search?page=`+params?.page;

      // Catégories
      let combinedCategoryIds = [];
      if (params.categoryId) combinedCategoryIds.push(params.categoryId.toString());
      if (params.filtre && params.filtre.length > 0) {
        const filterIds = params.filtre.map((id) => id.toString());
        combinedCategoryIds = [...combinedCategoryIds, ...filterIds];
      }
      combinedCategoryIds = [...new Set(combinedCategoryIds)];

      // Body
      const requestBody = {
        category_ids: combinedCategoryIds.length > 0 ? combinedCategoryIds : null,
        search_query: params.query || null,
        lieu_intervention: params.how || null,
        disponibilite: params.when || null,
        lat: params.lat || null,
        lng: params.lng || null,
        radius: 30,
        sort_by: params.sortBy || "relevance",
        location: params.location || null,
        cityName: params.cityName || null,
        filtre: params.filtre || [],
        per_page: params.per_page || 10,
        page: params.page || 1,
      };

      const response = await axios.post(apiUrl, requestBody, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const results = {
        local: response?.data?.local?.data || [],
        google: response?.data?.google || [],
        total: response?.data?.local?.total ?? 0,
        per_page: response?.data?.local?.per_page ?? 10,
        last_page: response?.data?.local?.last_page ?? '',
        pagination: response?.data?.pagination || null,
      };

      dispatch(setSearchResults(results));
      return results;
    } catch (error) {
      if (error.response) {
        return rejectWithValue(error.response.data);
      } else if (error.request) {
        return rejectWithValue("No response received from the server");
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

export const fetchAutocompleteSuggestions = createAsyncThunk(
  "search/autocomplete",
  async (searchParams = {}, { rejectWithValue }) => {
    try {
      const { query = "" } = searchParams;
      const apiUrl = `${process.env.REACT_APP_API_URL}search/autocomplete${
        query ? `?q=${encodeURIComponent(query)}` : ""
      }`;
      const response = await fetch(apiUrl);

      const contentType = response.headers.get("content-type");
      if (!response.ok) throw new Error(`HTTP Error: ${response.status}`);
      if (!contentType || !contentType.includes("application/json")) {
        throw new Error("Invalid response format");
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// --- Slice ---
const searchSlice = createSlice({
  name: "search",
  initialState,
  reducers: {
    updateSearchField: (state, action) => {
      const { field, value } = action.payload;
      state[field] = value;
      state.currentSearchParams[field] = value;
    },
    setSearchParams: (state, action) => {
      const newParams = action.payload;
      state.currentSearchParams = { ...state.currentSearchParams, ...newParams };

      Object.keys(newParams).forEach((key) => {
        if (state.hasOwnProperty(key)) {
          state[key] = newParams[key];
        }
      });
    },
    setSearchResults: (state, action) => {
      state.results = Array.isArray(action.payload.local) ? action.payload.local : [];
      state.googleResults = Array.isArray(action.payload.google) ? action.payload.google : [];
      state.total = action.payload.total ?? 0;
      state.per_page = action.payload.per_page ?? 10;
      state.last_page = action.payload.last_page ?? '';
      state.loading = false;
      state.error = null;
    },
    setLoading: (state) => {
      state.loading = true;
      state.error = null;
    },
    setError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },
    updateLocationCoords: (state, action) => {
      const { lat, lng, location, cityName } = action.payload;
      state.lat = lat;
      state.lng = lng;
      state.location = location;
      state.cityName = cityName;
      state.currentSearchParams.lat = lat;
      state.currentSearchParams.lng = lng;
      state.currentSearchParams.location = location;
      state.currentSearchParams.cityName = cityName;
    },
    updateFilters: (state, action) => {
      state.filtre = action.payload.map((id) => id.toString());
      state.currentSearchParams.filtre = action.payload.map((id) => id.toString());
    },
    setCategoryName: (state, action) => {
      state.categoryName = action.payload;
    },
    setSortBy: (state, action) => {
      state.sortBy = action.payload;
      state.currentSearchParams.sortBy = action.payload;
    },
    setPage: (state, action) => {
      state.page = action.payload;
      state.currentSearchParams.page = action.payload;
    },
    setPerPage: (state, action) => {
      state.per_page = action.payload;
      state.last_page = action.payload;
      state.currentSearchParams.per_page = action.payload;
    },
    resetSearchParams: () => {
      return {
        ...initialState,
        currentSearchParams: {},
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLocationCoords.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLocationCoords.fulfilled, (state, action) => {
        state.lat = action.payload.lat;
        state.lng = action.payload.lng;
        state.currentSearchParams.lat = action.payload.lat;
        state.currentSearchParams.lng = action.payload.lng;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchLocationCoords.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchAutocompleteSuggestions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAutocompleteSuggestions.fulfilled, (state, action) => {
        state.autocompleteSuggestions = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchAutocompleteSuggestions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchSearchResults.pending, (state) => {
        state.loadingSearch = true;
        state.error = null;
      })
      .addCase(fetchSearchResults.fulfilled, (state, action) => {
        state.results = Array.isArray(action.payload.local) ? action.payload.local : [];
        state.googleResults = Array.isArray(action.payload.google) ? action.payload.google : [];
        state.total = action.payload.total ?? 0;
        state.per_page = action.payload.per_page ?? 10;
        state.last_page = action.payload.last_page ?? '';
        state.loadingSearch = false;
        state.error = null;

        if (action.payload.pagination) {
          state.page = action.payload.pagination.current_page;
          state.per_page = action.payload.pagination.per_page;
          state.last_page = action.payload.pagination.last_page
          state.currentSearchParams.page = action.payload.pagination.current_page;
          state.currentSearchParams.per_page = action.payload.pagination.per_page;
        }
      })
      .addCase(fetchSearchResults.rejected, (state, action) => {
        state.loadingSearch = false;
        state.error = action.payload;
      });
  },
});

export const {
  updateSearchField,
  setSearchParams,
  setSearchResults,
  setLoading,
  setError,
  updateLocationCoords,
  updateFilters,
  setCategoryName,
  setSortBy,
  setPage,
  setPerPage,
  resetSearchParams,
} = searchSlice.actions;

export default searchSlice.reducer;
